import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { filter, take } from 'rxjs';

import { IconComponent } from '@shared/lib/components';
import { CloudService, DerivedAuthService } from '@shared/public-api';
import { environment } from 'projects/portal/src/environments/environment';

import { HtmlToTailwindPipe } from '../../pipes/html-to-tailwind.pipe';
import { LinkService } from '../../services/link.service';
import { ToasterService } from '../../services/toaster.service';

interface INotifications {
  id: string | number;
  title?: string;
  description?: string;
  startDate: string;
  endDate: string;
  sourceId?: string;
  [key: string]: unknown;
}

interface IAuditLogParams {
  logType: string;
  email: string | null | undefined;
  userId: string | null | undefined;
  userType: string;
  eventName: string;
  activityStatus: string;
  operationalStatus: string;
  payLoad: string;
  comments: string;
  eventType: string;
}
@Component({
  selector: 'wu-toaster-notification',
  imports: [IconComponent, HtmlToTailwindPipe],
  templateUrl: './toaster-notification.component.html',

  styleUrl: './toaster-notification.component.scss',
})
export class ToasterNotificationComponent implements OnInit {
  private readonly derivedAuthService = inject(DerivedAuthService);
  private readonly linkService = inject(LinkService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cloudService = inject(CloudService);
  private readonly authToken$ = toObservable(this.derivedAuthService.authToken);
  readonly toasterService = inject(ToasterService);
  readonly bigQueryParam = {
    studentParam: 'STUDENT',
    afterExecutionParam: 'AFTER EXECUTION',
    LogType: 'INFO',
  };

  readonly bigQueryErrorParam = {
    studentParam: 'STUDENT',
    afterExecutionParam: 'AFTER EXECUTION',
    LogType: 'ERROR',
  };

  readonly notificationError = {
    action: 'Get portalNotifications Data',
    LogType: 'ERROR',
  };

  readonly notificationFirestoreParam = {
    action: 'Get Firestore Data',
    LogType: 'INFO',
  };

  readonly studentId = this.derivedAuthService.studentId;
  readonly currentUser = this.derivedAuthService.currentUser;

  readonly notificationData = signal<INotifications[]>([]);

  private processNotificationItems(items: INotifications[]): void {
    if (!items.length) {
      this.notificationData.set([]);
      const desc = 'portal notification: FireStore Collection does not exist';
      this.addtoStack(
        this.currentUser()?.email,
        this.notificationFirestoreParam.action,
        this.notificationFirestoreParam.LogType,
        desc,
      );
      this.auditLog({
        logType: this.bigQueryParam.LogType,
        email: this.currentUser()?.email?.toLowerCase(),
        userId: this.currentUser()?.uid,
        userType: this.bigQueryParam.studentParam,
        eventName: 'notificationFirestoreData',
        activityStatus: this.bigQueryParam.afterExecutionParam,
        operationalStatus: 'No Data',
        payLoad: desc,
        comments: 'LOG INFO FOR NOTIFICATION FIRESTORE',
        eventType: 'notificationFirestoreData : No data',
      });
      return;
    }

    const activeNotifications = items
      .filter(item => this.isActiveNotification(item))
      .filter(
        item =>
          !this.toasterService.isDismissed(
            String(item.id ?? `${item.title}|${item.startDate}`),
          ),
      )
      .map((item, index) => ({
        ...item,
        sourceId: String(item.id ?? `${item.title}|${item.startDate}`),
        id: index,
      }));

    this.notificationData.set(activeNotifications);

    this.toasterService.clear();
    for (const item of activeNotifications) {
      this.toasterService.add({
        title: item.title,
        description: item.description,
        sourceId: item.sourceId,
      });
    }

    const desc = 'portal notification : Data fetched from Firestore Collection';
    this.addtoStack(
      this.currentUser()?.email,
      this.notificationFirestoreParam.action,
      this.notificationFirestoreParam.LogType,
      desc,
    );
    this.auditLog({
      logType: this.bigQueryParam.LogType,
      email: this.currentUser()?.email?.toLowerCase(),
      userId: this.currentUser()?.uid,
      userType: this.bigQueryParam.studentParam,
      eventName: 'notificationFirestoreData',
      activityStatus: this.bigQueryParam.afterExecutionParam,
      operationalStatus: 'Data fetched',
      payLoad: desc,
      comments: 'LOG INFO FOR NOTIFICATION',
      eventType: 'notificationFirestoreData : Success',
    });
  }

  private handleNotificationError(error: Error): void {
    console.error('Error fetching notifications:', error);
    this.notificationData.set([]);
    const msg = error.message ?? String(error);
    const desc = `portal notification: Error fetching data from firestore -- "${msg}"`;
    this.addtoStack(
      this.derivedAuthService.currentUser()?.email?.toLowerCase(),
      this.notificationError.action,
      this.notificationError.LogType,
      desc,
    );
    this.auditLog({
      logType: this.bigQueryErrorParam.LogType,
      email: this.derivedAuthService.currentUser()?.email?.toLowerCase(),
      userId: this.derivedAuthService.currentUser()?.uid,
      userType: this.bigQueryErrorParam.studentParam,
      eventName: 'notificationFirestoreData',
      activityStatus: this.bigQueryErrorParam.afterExecutionParam,
      operationalStatus: 'Failed to fetch data',
      payLoad: desc,
      comments: 'LOG ERROR FOR NOTIFICATION FIRESTORE',
      eventType: 'notificationFirestoreData : Success',
    });
  }

  ngOnInit(): void {
    // If the token is already available synchronously (common on page reload
    // when window.__PORTAL_AUTH_STATE__ is already set), fetch immediately.
    // Otherwise wait for the token signal to become truthy.
    if (this.derivedAuthService.authToken()) {
      this.getNotificationFromFireStore();
    } else {
      this.authToken$
        .pipe(
          filter(token => !!token),
          take(1),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => this.getNotificationFromFireStore());
    }
  }

  isActiveNotification(item: INotifications): boolean {
    const estNow = new Date(
      new Date().toLocaleString('en-US', {
        timeZone: 'America/New_York',
      }),
    ).getTime();

    const startDate = new Date(item.startDate).getTime();
    const endDate = new Date(item.endDate).getTime();

    return startDate <= estNow && endDate >= estNow;
  }

  getNotificationFromFireStore(): void {
    this.linkService
      .getNotification()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: unknown) => {
          const data = response as { data?: { items?: INotifications[] } };
          const items: INotifications[] = data?.data?.items ?? [];
          this.processNotificationItems(items);
        },
        error: (error: Error) => {
          this.handleNotificationError(error);
        },
      });
  }

  addtoStack(
    id: string | null | undefined,
    action: string,
    logType: string,
    description: string,
  ): void {
    const stackData = {
      email: id,
      activity: action,
      logType: logType,
      description: description,
    };
    const baseUrlDetails = environment.cloudFunction.baseUrl;
    this.cloudService.postDataToStack(
      baseUrlDetails,
      stackData,
      () => {},
      (err: unknown) => {
        console.error(err);
      },
    );
  }

  auditLog(params: IAuditLogParams): void {
    const auditData = {
      logType: params.logType,
      email: params.email,
      userId: params.userId,
      bannerId: this.studentId(),
      userType: params.userType,
      eventName: params.eventName,
      activityStatus: params.activityStatus,
      operationalStatus: params.operationalStatus,
      payLoad: params.payLoad,
      comments: params.comments,
      createdBy: 'Portal Refresh',
      eventType: params.eventType,
    };
    const baseUrlDetails = environment.cloudFunction.baseUrl;
    this.cloudService.postDataToBigQuery(
      baseUrlDetails,
      auditData,
      () => {},
      (err: unknown) => {
        console.error(err);
      },
    );
  }
}
